import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import { createH1SliderSwipeHandlers, sliderTrackTransform } from '../utils/h1-slider-swipe';
import { MarketingStoryDetailModalComponent } from './marketing-story-detail-modal.component';
import { needsReadMoreExcerpt } from './shared/marketing-read-more.utils';
import {
  detailFromTestimonial,
  type MarketingStoryDetail,
  type MarketingTestimonialItem,
} from './shared/marketing-story-detail.model';

export type { MarketingTestimonialItem } from './shared/marketing-story-detail.model';

/** Avoid a last slide with a single card (e.g. 4 → 2+2, 7 → 3+2+2). */
function buildTestimonialSlides(items: MarketingTestimonialItem[]): MarketingTestimonialItem[][] {
  const n = items.length;
  if (n === 0) return [];
  if (n <= 3) return [items];

  if (n % 3 === 1) {
    const groups: MarketingTestimonialItem[][] = [];
    const leadingCount = n - 4;
    for (let start = 0; start < leadingCount; start += 3) {
      groups.push(items.slice(start, start + 3));
    }
    const tail = leadingCount;
    groups.push(items.slice(tail, tail + 2));
    groups.push(items.slice(tail + 2, tail + 4));
    return groups;
  }

  const groups: MarketingTestimonialItem[][] = [];
  for (let i = 0; i < n; i += 3) {
    groups.push(items.slice(i, i + 3));
  }
  return groups;
}

@Component({
  selector: 'app-marketing-testimonials',
  standalone: true,
  imports: [NgStyle, MarketingStoryDetailModalComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: contents' },
  templateUrl: './marketing-testimonials.component.html',
})
export class MarketingTestimonialsComponent implements OnChanges {
  @Input() items: MarketingTestimonialItem[] = [];

  @ViewChild('viewport') private viewport?: ElementRef<HTMLElement>;

  readonly testimonialSlide = signal(0);
  readonly dragOffsetPx = signal(0);
  readonly isDragging = signal(false);
  readonly selectedDetail = signal<MarketingStoryDetail | null>(null);

  readonly testimonialSlides = computed(() => buildTestimonialSlides(this.items));

  readonly swipe = createH1SliderSwipeHandlers({
    activeSlide: this.testimonialSlide,
    slideCount: () => this.testimonialSlides().length,
    viewportWidth: () => this.viewport?.nativeElement.clientWidth ?? 0,
    dragOffsetPx: this.dragOffsetPx,
    isDragging: this.isDragging,
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      const max = Math.max(0, this.testimonialSlides().length - 1);
      if (this.testimonialSlide() > max) {
        this.testimonialSlide.set(max);
      }
    }
  }

  trackTransform(): string {
    return sliderTrackTransform(this.testimonialSlide(), this.dragOffsetPx());
  }

  prevTestimonialSlide(): void {
    this.testimonialSlide.update((s) => Math.max(0, s - 1));
  }

  nextTestimonialSlide(): void {
    this.testimonialSlide.update((s) => Math.min(this.testimonialSlides().length - 1, s + 1));
  }

  setTestimonialSlide(i: number): void {
    this.testimonialSlide.set(i);
  }

  needsReadMore(review: string): boolean {
    return needsReadMoreExcerpt(review);
  }

  openDetail(item: MarketingTestimonialItem): void {
    this.selectedDetail.set(detailFromTestimonial(item));
  }

  closeDetail(): void {
    this.selectedDetail.set(null);
  }

  slideGridClass(count: number): string {
    if (count === 1) return 'h1-testimonials h1-testimonials--count-1';
    if (count === 2) return 'h1-testimonials h1-testimonials--count-2';
    return 'h1-testimonials';
  }

  /** Line under name — course field or university · country (like /home1). */
  subtitle(t: MarketingTestimonialItem): string {
    const course = t.course?.trim();
    if (course) return course;
    return [t.university?.trim(), t.country?.trim()].filter(Boolean).join(' · ');
  }

  hasAvatar(t: MarketingTestimonialItem): boolean {
    return !!t.avatar?.cdnUrl?.trim();
  }

  avatarStyle(t: MarketingTestimonialItem): Record<string, string> | null {
    const url = t.avatar?.cdnUrl?.trim();
    return url ? { 'background-image': `url(${url})` } : null;
  }

  initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return parts
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('');
  }
}
